import React, { useState } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export default function SetupPage({ onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminPass2, setAdminPass2] = useState('');

  const inputStyle = { width: '100%', fontFamily: 'DM Sans', fontSize: 14, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, outline: 'none', background: 'var(--surface)', boxSizing: 'border-box', marginTop: 6 };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block' };

  const createAdmin = async () => {
    if (adminPass !== adminPass2) { setError('Passwords do not match'); return; }
    if (adminPass.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/setup/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user: adminUser, admin_pass: adminPass }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Setup failed');
      setStep(2);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460 }}>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="var(--accent)">
              <path d="M11.53 2c0 2.4 1.97 4.34 4.34 4.34h1.76v1.7c0 2.4 1.94 4.34 4.37 4.35V2.69a.7.7 0 0 0-.7-.69H11.53zM6.77 6.8a4.36 4.36 0 0 0 4.34 4.34h1.76v1.71a4.36 4.36 0 0 0 4.37 4.35V7.49a.7.7 0 0 0-.7-.69H6.77zM2 11.62a4.36 4.36 0 0 0 4.34 4.34h1.76v1.7A4.36 4.36 0 0 0 12.47 22v-9.31a.7.7 0 0 0-.7-.69H2z"/>
            </svg>
            <span style={{ fontSize: 22, fontWeight: 700 }}>Jira Dashboard</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)' }}>First-time setup</div>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          {[{ n: 1, label: 'Admin Account' }, { n: 2, label: 'Jira Session' }].map(({ n, label }, i) => (
            <React.Fragment key={n}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: step >= n ? 'var(--accent)' : 'var(--border)', color: step >= n ? '#fff' : 'var(--text-2)', transition: 'all .2s' }}>{n}</div>
                <span style={{ fontSize: 12, color: step === n ? 'var(--text)' : 'var(--text-2)', fontWeight: step === n ? 600 : 400 }}>{label}</span>
              </div>
              {i < 1 && <div style={{ flex: 1, height: 1, background: step > n ? 'var(--accent)' : 'var(--border)' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="card">
          {step === 1 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Create Admin Account</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
                Protects access to dashboard settings. Choose any username and a strong password.
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Username</label>
                <input style={inputStyle} type="text" value={adminUser} onChange={e => setAdminUser(e.target.value)} placeholder="admin" autoComplete="username" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" value={adminPass} onChange={e => setAdminPass(e.target.value)} placeholder="Min. 6 characters" autoComplete="new-password" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Confirm password</label>
                <input style={inputStyle} type="password" value={adminPass2} onChange={e => setAdminPass2(e.target.value)} placeholder="Repeat password"
                  onKeyDown={e => e.key === 'Enter' && createAdmin()} />
              </div>
              {error && <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--critical-light)', borderRadius: 8, color: 'var(--critical)', fontSize: 13 }}>{error}</div>}
              <button className="btn btn-primary" onClick={createAdmin} disabled={loading || !adminUser || !adminPass}
                style={{ width: '100%', justifyContent: 'center', opacity: (!adminUser || !adminPass) ? .5 : 1 }}>
                {loading ? 'Saving…' : 'Continue →'}
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Connect to Jira</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20, lineHeight: 1.6 }}>
                Since your Jira uses Azure AD, we use your browser session cookie to authenticate. You'll need to refresh it periodically (typically every 8–24 hours).
              </div>

              <div style={{ background: 'var(--accent-light)', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 12, lineHeight: 1.8 }}>
                <strong style={{ fontSize: 13 }}>How to get your session cookie:</strong><br/>
                1. Open <strong>infra.bewan.be</strong> and log in via Azure AD<br/>
                2. Press <strong>F12</strong> → Application → Cookies → <code style={{ background: 'rgba(0,0,0,.06)', padding: '1px 5px', borderRadius: 4 }}>https://infra.bewan.be</code><br/>
                3. Find <strong>JSESSIONID</strong> → copy the value<br/>
                4. Paste it below
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 20, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8 }}>
                🔒 The cookie is encrypted with AES-128 before being stored on the server. It is never visible in plain text.
              </div>

              <button className="btn btn-primary" onClick={onComplete} style={{ width: '100%', justifyContent: 'center' }}>
                Go to Dashboard — I'll add the cookie there →
              </button>
              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: 'var(--text-2)' }}>
                You can paste the cookie anytime from the <strong>Upload / Settings</strong> page
              </div>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'var(--text-2)' }}>
          Credentials encrypted with AES-128 (Fernet) · Key stored only in Docker memory
        </div>
      </div>
    </div>
  );
}
